import { create, v, w } from '@dojo/framework/core/vdom';
import { theme } from '@dojo/framework/core/middleware/theme';
import has from '@dojo/framework/core/has';
import { Button } from '@dojo/widgets/button';
import { numeral } from '@holmescorp/numeral';
import { Keys } from '@holmescorp/common';
import { lccstate, userstate, createNlsMiddleware, bonusquizzes } from '@holmescorp/lcc-state';
import { publish } from '@holmescorp/pub-sub';
import { resourceDitabookSelector } from '@holmescorp/resource-parser';
import { PrePostViz } from '@holmescorp/prepost-viz';
import { Icon } from '@holmescorp/base-widgets/icon';
import { YouAreHere } from './you-are-here';
import css from '../css/practice-view.m.css';
import { formatAriaProperties } from '@dojo/widgets/common/util';
import { BackView } from '@holmescorp/backview';
const nls = createNlsMiddleware('ditabook');
const settings = createNlsMiddleware('settings');
const quizNls = createNlsMiddleware('quiz');
const factory = create({ theme, nls, lccstate, userstate, settings, quizNls }).properties();
export const PracticeView = factory(({ properties, middleware: { theme, lccstate, userstate, nls, settings, quizNls } }) => {
    const themeCss = theme.classes(css);
    const { switchView } = properties();
    const { format, messages: { practice_type = 'practice', practice_button = '[ditabook] practice_button', retake_practice = '[ditabook] retake_practice', practice_bonus_label = '[ditabook] practice_bonus_label', practice_bonus_description = '[ditabook] practice_bonus_description', practice_back_label = '[ditabook] practice_back_label' } } = nls();
    const { messages: settingsMessages } = settings();
    const { messages: { quiz_disclaimer = '' } } = quizNls();
    const lockIcon = settingsMessages.is_demo
        ? v('span', { classes: [themeCss.lockIcon] }, [w(Icon, { faname: 'lock' })])
        : null;
    const isPracticeOnly = has('use-alternate-pass-view');
    const useCutscores = has('use-cutscores');
    const { get, path } = userstate;
    const get_tree = (practice_type) => {
        const quizstate = get(path(practice_type));
        switch (practice_type) {
            case 'practice':
                return quizstate;
            case 'posttest':
                return quizstate.tree;
        }
        return {};
    };
    const tree = get_tree(practice_type);
    const tgfkeys = Object.keys(tree).filter((k) => tree[k].parent !== null);
    const has_scores = () => {
        const roots = lccstate.get(lccstate.path('ditaroots'));
        return roots.some((k) => tree[k].data.percent > 0);
    };
    const hasPosttest = (tgfkeys.length > 0 || isPracticeOnly) && has_scores();
    const quizpath = userstate.get(userstate.path('quizPattern'));
    const pathid = quizpath && quizpath.length > 0 ? settingsMessages[`path-${quizpath}`] || '' : '';
    const posttestPending = () => {
        return [
            w(resourceDitabookSelector, {
                topic: 'practice_invite',
                key: 'practice-invite',
                classes: [themeCss.about],
                formatting: { pathid }
            }),
            v('div', { key: 'actions', classes: [themeCss.button] }, [
                w(Button, {
                    onClick: () => {
                        if (!settingsMessages.is_demo) {
                            publish(`site/${practice_type}`);
                        }
                    }
                }, [
                    {
                        icon: lockIcon,
                        label: practice_button
                    }
                ])
            ])
        ];
    };
    const posttestComplete = () => {
        const whichtest = practice_type;
        let percentscore = '0%';
        let quizType = '';
        if (whichtest === 'posttest') {
            const score = get(path('posttest', 'score'));
            percentscore = numeral(score / 100.0).format('0%');
            quizType = 'Post-Test';
        }
        else {
            const tree = get(path('practice'));
            const points = Object.keys(tree).reduce((accum, key) => {
                if (tree[key].parent === null) {
                    accum.correct += tree[key].data.pointscorrect;
                    accum.incorrect += tree[key].data.pointsincorrect;
                }
                return accum;
            }, {
                correct: 0,
                incorrect: 0
            });
            const score = points.correct / (points.correct + points.incorrect);
            percentscore = numeral(score).format('0%');
            quizType = 'Practice Exam';
        }
        return [
            w(resourceDitabookSelector, {
                topic: 'practice_completed',
                key: 'assess-practice',
                xqa: 'practice-completed-score',
                classes: [themeCss.about],
                formatting: { pathid, percent: percentscore, quiz: quizType }
            }),
            v('div', {
                key: 'score-title',
                classes: [themeCss.scoreBlockTitle],
                innerHTML: format('practice_score_title', { pathid })
            }),
            isPracticeOnly && useCutscores
                ? w('CutScores', { label: quizType, modules: tree, quizPattern: quizpath })
                : w(PrePostViz, { whichtest }),
            v('div', { classes: [themeCss.remind] }, [
                v('div', {
                    role: 'button',
                    tabIndex: 0,
                    classes: [themeCss.retake],
                    onclick: (event) => {
                        event.stopPropagation();
                        publish(`site/${practice_type}`);
                    },
                    onkeydown: (event) => {
                        switch (event.key) {
                            case Keys.Enter:
                            case Keys.Space:
                            case Keys.Spacebar:
                                event.preventDefault();
                                publish(`site/${practice_type}`);
                        }
                    }
                }, [retake_practice])
            ]),
            quiz_disclaimer
                ? v('div', {
                    key: 'disclaimer',
                    id: 'quiz-disclaimer',
                    classes: [themeCss.disclaimer],
                    innerHTML: quiz_disclaimer
                })
                : null
        ];
    };
    const extra_quizzes = () => {
        const content = [
            v('h3', {}, [practice_bonus_label]),
            v('div', { classes: [themeCss.bonusDescription], innerHTML: practice_bonus_description })
        ];
        const quizzes = Array.from(bonusquizzes).map((item) => {
            const [key, { title }] = item;
            return v('div', {
                key,
                classes: [themeCss.bonus],
                onclick: (event) => {
                    event.stopPropagation();
                    publish('site/bonusquiz', key);
                }
            }, [title]);
        });
        if (quizzes.length > 0) {
            return [...content, ...quizzes];
        }
        else {
            return [];
        }
    };
    const posttestKids = hasPosttest ? posttestComplete() : posttestPending();
    const overrideTitle = practice_back_label;
    const backWidget = v('div', { classes: [themeCss.back] }, [
        w(BackView, { overrideSelected: () => publish('active-view', 'study'), overrideTitle })
    ]);
    const tabsOrBack = isPracticeOnly ? backWidget : w(YouAreHere, { active: 'practice', switchView });
    return v('div', { key: 'practice-view', classes: [themeCss.root], xqa: 'view practice' }, [
        tabsOrBack,
        v('div', {
            id: 'tabPanelPractice',
            ...formatAriaProperties({
                labelledby: 'practiceTab'
            }),
            key: 'content',
            classes: [themeCss.content]
        }, [
            ...posttestKids,
            w(resourceDitabookSelector, {
                topic: 'practice_extra',
                key: 'extra-practice',
                classes: [themeCss.practiceExtra]
            })
        ]),
        v('div', { key: 'bonus-block', classes: [themeCss.bonusBlock] }, extra_quizzes())
    ]);
});
